import { useState, useMemo, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, ArrowRight, ArrowLeft, Sparkles, Check, AlertCircle } from 'lucide-react';
import { ShelfMappingRow } from './ShelfMappingRow';
import { ImportAnalysisSummary } from './ImportAnalysisSummary';
import { useImportAnalysis } from '@/hooks/useImportAnalysis';
import type { AnalysisResult, ShelfMapping, ShelfAnalysis } from '@/lib/shelfMappingUtils';

interface ShelfMappingWizardProps {
  importJobId: string;
  userId: string;
  onComplete: () => void;
  onBack?: () => void;
}

export function ShelfMappingWizard({ 
  importJobId, 
  userId, 
  onComplete, 
  onBack 
}: ShelfMappingWizardProps) {
  const { analyzing, analysis, error, analyzeImport, saveMappings } = useImportAnalysis();
  const [step, setStep] = useState<'loading' | 'mapping' | 'saving'>('loading');
  const [mappings, setMappings] = useState<Map<string, ShelfMapping>>(new Map());
  const [saveError, setSaveError] = useState<string | null>(null);

  // Start analysis on mount
  useEffect(() => {
    analyzeImport(importJobId);
  }, [importJobId]);

  // Initialize mappings when analysis completes
  useEffect(() => {
    if (analysis) {
      const initialMappings = new Map<string, ShelfMapping>();
      
      // Add exclusive shelves with their suggestions
      analysis.exclusiveShelves.forEach((shelf) => {
        initialMappings.set(shelf.name, {
          goodreadsShelf: shelf.name,
          targetType: shelf.suggestedMapping.type,
          targetValue: shelf.suggestedMapping.value,
          bookCount: shelf.bookCount,
        });
      });
      
      // Add custom shelves with their suggestions
      analysis.customShelves.forEach((shelf) => {
        initialMappings.set(shelf.name, {
          goodreadsShelf: shelf.name,
          targetType: shelf.suggestedMapping.type,
          targetValue: shelf.suggestedMapping.value,
          bookCount: shelf.bookCount,
        });
      });
      
      setMappings(initialMappings);
      setStep('mapping');
    }
  }, [analysis]);

  const handleMappingChange = (shelfName: string, mapping: ShelfMapping) => {
    setMappings(prev => {
      const next = new Map(prev);
      next.set(shelfName, mapping);
      return next;
    });
  };

  const handleConfirm = async () => {
    setStep('saving');
    setSaveError(null);

    const mappingsArray = Array.from(mappings.values());
    const success = await saveMappings(importJobId, userId, mappingsArray);

    if (success) {
      onComplete();
    } else {
      setSaveError('Failed to save your shelf mappings. Please try again.');
      setStep('mapping');
    }
  };

  // Count how many mappings go to each destination
  const mappingSummary = useMemo(() => {
    const summary = {
      systemShelves: 0,
      customShelves: 0,
      tags: 0,
      skipped: 0,
    };
    
    mappings.forEach((m) => {
      switch (m.targetType) {
        case 'system_shelf': summary.systemShelves++; break;
        case 'custom_shelf': summary.customShelves++; break;
        case 'tag': summary.tags++; break;
        case 'skip': summary.skipped++; break;
      }
    });
    
    return summary;
  }, [mappings]);

  // Check if there are any custom shelves to map
  const hasCustomShelves = analysis && analysis.customShelves.length > 0;

  // All shelves combined for display
  const allShelves: ShelfAnalysis[] = analysis 
    ? [...analysis.exclusiveShelves, ...analysis.customShelves]
    : [];

  if (step === 'loading' || analyzing) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Loader2 className="w-8 h-8 mx-auto animate-spin text-primary mb-4" />
          <h3 className="text-lg font-medium mb-2">Analyzing your library...</h3>
          <p className="text-muted-foreground">
            Finding shelves and preparing smart suggestions
          </p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8">
          <Alert variant="destructive">
            <AlertCircle className="w-4 h-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
          <div className="flex justify-center mt-4">
            {onBack && (
              <Button variant="outline" onClick={onBack}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Go Back
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!analysis) {
    return null;
  }

  // If no custom shelves, just show summary and continue
  if (!hasCustomShelves && analysis.exclusiveShelves.length <= 3) {
    return (
      <Card>
        <CardHeader className="text-center">
          <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-2">
            <Check className="w-6 h-6 text-primary" />
          </div>
          <CardTitle>Ready to Import!</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <ImportAnalysisSummary analysis={analysis} />
          <p className="text-center text-muted-foreground">
            Your Goodreads shelves will be mapped automatically to Dino's system.
          </p>
          <div className="flex gap-3 justify-center">
            {onBack && (
              <Button variant="outline" onClick={onBack}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            )}
            <Button onClick={onComplete}>
              Start Import
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="space-y-2">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-primary" />
          <CardTitle className="text-xl">Map Your Shelves</CardTitle>
        </div>
        <p className="text-muted-foreground">
          We found custom shelves in your export. Tell us how they should work in Dino:
        </p>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Analysis Summary */}
        <ImportAnalysisSummary analysis={analysis} />

        {/* Mapping Rows */}
        <div className="space-y-2">
          <h3 className="font-medium text-sm">Shelf Mappings</h3>
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-3">
              {allShelves.map((shelf) => {
                const mapping = mappings.get(shelf.name);
                if (!mapping) return null;
                
                return (
                  <ShelfMappingRow
                    key={shelf.name}
                    shelf={shelf}
                    mapping={mapping}
                    onMappingChange={(m) => handleMappingChange(shelf.name, m)}
                  />
                );
              })}
            </div>
          </ScrollArea>
        </div>

        {/* Summary of selections */}
        <div className="flex flex-wrap gap-2 text-sm text-muted-foreground pt-2 border-t">
          {mappingSummary.systemShelves > 0 && (
            <span>📚 {mappingSummary.systemShelves} system shelves</span>
          )}
          {mappingSummary.customShelves > 0 && (
            <span>📖 {mappingSummary.customShelves} custom shelves</span>
          )}
          {mappingSummary.tags > 0 && (
            <span>🏷️ {mappingSummary.tags} tags</span>
          )}
          {mappingSummary.skipped > 0 && (
            <span className="text-muted-foreground/60">⏭️ {mappingSummary.skipped} skipped</span>
          )}
        </div>

        {saveError && (
          <Alert variant="destructive">
            <AlertCircle className="w-4 h-4" />
            <AlertDescription>{saveError}</AlertDescription>
          </Alert>
        )}

        {/* Actions */}
        <div className="flex gap-3 justify-end pt-2">
          {onBack && (
            <Button variant="outline" onClick={onBack} disabled={step === 'saving'}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          )}
          <Button onClick={handleConfirm} disabled={step === 'saving'}>
            {step === 'saving' ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                Confirm & Import
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
